import React, { useContext, useState } from "react";

// react-router-dom components
import { Link, Navigate } from "react-router-dom";

// @mui material components
import Switch from "@mui/material/Switch";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import AuthService from "services/auth-service";
import { AuthContext } from "context";

// Authentication layout components
import IllustrationLayout from "layouts/authentication/components/IllustrationLayout";
import logoImage from "assets/images/illustrations/logo.png";

// Video background
import bgVideo from "assets/images/illustrations/Filenders.m4v";

function Login() {
  const { isAuthenticated, isLoading, login } = useContext(AuthContext);

  const [rememberMe, setRememberMe] = useState(false);
  const [inputs, setInputs] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    emailError: false,
    passwordError: false,
    credentialsErros: false,
    textError: "",
  });

  if (isAuthenticated) {
    return <Navigate to="/dashboard/analytics" />;
  }

  if (isLoading) return null;

  const handleSetRememberMe = () => setRememberMe((prev) => !prev);

  const changeHandler = (e) => {
    const { name, value } = e.target;

    setInputs((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      emailError: name === "email" ? false : prev.emailError,
      passwordError: name === "password" ? false : prev.passwordError,
      credentialsErros: false,
      textError: "",
    }));
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    const mailFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    let hasError = false;

    if (
        inputs.email.trim().length === 0 ||
        !mailFormat.test(inputs.email.trim())
    ) {
      setErrors((prev) => ({
        ...prev,
        emailError: true,
      }));
      hasError = true;
    }

    if (inputs.password.trim().length < 6) {
      setErrors((prev) => ({
        ...prev,
        passwordError: true,
      }));
      hasError = true;
    }

    if (hasError) return;

    const newUser = {
      email: inputs.email.trim(),
      password: inputs.password,
    };

    const myData = {
      data: {
        type: "token",
        attributes: { ...newUser },
      },
    };

    try {
      const response = await AuthService.login(myData);

      if (!response || !response.access_token) {
        setErrors((prev) => ({
          ...prev,
          credentialsErros: true,
          textError: "Invalid response from server.",
        }));
        return;
      }

      login(response.access_token, response.refresh_token);
    } catch (res) {
      setErrors((prev) => ({
        ...prev,
        credentialsErros: true,
        textError:
            res?.message ||
            res?.errors?.[0]?.detail ||
            "Login failed. Please try again.",
      }));
    }
  };

  return (
      <IllustrationLayout
          logo={logoImage}
          title="Login to Filentech"
          description="Enter your email and password to sign in"
          illustration={bgVideo}
      >
        <MDBox
            component="form"
            role="form"
            method="POST"
            onSubmit={submitHandler}
        >
          <MDBox mb={2}>
            <MDInput
                type="email"
                label="Email"
                fullWidth
                variant="standard"
                name="email"
                value={inputs.email}
                onChange={changeHandler}
                error={errors.emailError}
                InputLabelProps={{ shrink: true }}
            />
          </MDBox>

          <MDBox mb={2}>
            <MDInput
                type="password"
                label="Password"
                fullWidth
                variant="standard"
                name="password"
                value={inputs.password}
                onChange={changeHandler}
                error={errors.passwordError}
                InputLabelProps={{ shrink: true }}
            />
          </MDBox>

          <MDBox display="flex" alignItems="center" ml={-1}>
            <Switch checked={rememberMe} onChange={handleSetRememberMe} />
            <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
            >
              &nbsp;&nbsp;Remember me
            </MDTypography>
          </MDBox>

          {errors.credentialsErros && (
              <MDTypography variant="caption" color="error" fontWeight="light">
                {errors.textError}
              </MDTypography>
          )}

          <MDBox mt={4} mb={1}>
            <MDButton
                variant="gradient"
                color="info"
                size="large"
                fullWidth
                type="submit"
            >
              sign in
            </MDButton>
          </MDBox>

          <MDBox mt={3} mb={1} textAlign="center">
            <MDTypography variant="button" color="text">
              Forgot your password? Reset it{" "}
              <MDTypography
                  component={Link}
                  to="/auth/forgot-password"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
              >
                here
              </MDTypography>
            </MDTypography>
          </MDBox>
        </MDBox>
      </IllustrationLayout>
  );
}

export default Login;